import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BgBox from "../../common/BgBox";
import MobileAppImage from "../../assets/images/mobile_app_image.png";
import MccLogo from "../../assets/images/mcc-logo.png";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import BiosynciqFooter from "../../components/BiosynciqFooter";
import CommonStoreButton from "../../components/CommonStoreButton";
import CircularProgressLoading from "../../common/CircularProgress";
import { isIOS } from "react-device-detect";
import {
  buildAppStoreUrl,
  buildEnrollmentAppUrl,
  buildEnrollmentQuery,
  buildPracticeTokenAppUrl,
  copyTextToClipboard,
  isMobileDevice,
  openAppOrFallbackToStore,
  resolveEnrollmentToken,
  resolvePracticeToken,
} from "../../utils/helpers/enrollmentToken";

const AgencyQrCode = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const navigate = useNavigate();

  const enrollmentToken = searchParams.get("enrollment_token");
  const practiceToken = searchParams.get("token");
  const legacyAgencyId = searchParams.get("agency_id");
  const legacyAgencyName = searchParams.get("agency_name");
  const isMobile = isMobileDevice();

  const [agencyId, setAgencyId] = useState(legacyAgencyId);
  const [agencyName, setAgencyName] = useState(legacyAgencyName);
  const [loading, setLoading] = useState(
    Boolean(enrollmentToken || practiceToken),
  );
  const [error, setError] = useState(null);
  const [showAppStorePrompt, setShowAppStorePrompt] = useState(false);
  // Once the user cancels (OS sheet or our dialog), never show it again.
  const suppressStorePromptRef = useRef(false);

  useEffect(() => {
    if (!enrollmentToken && !practiceToken) return;

    let cancelled = false;
    let cancelAppOpen = () => {};

    const resolveLink = enrollmentToken
      ? resolveEnrollmentToken(enrollmentToken).then(
          ({ agencyId: id, agencyName: name }) => ({
            agencyId: id,
            agencyName: name,
            appUrl: buildEnrollmentAppUrl(enrollmentToken),
          }),
        )
      : resolvePracticeToken(practiceToken).then(
          ({ agencyId: id, agencyName: name }) => ({
            agencyId: id,
            agencyName: name,
            appUrl: buildPracticeTokenAppUrl(practiceToken),
          }),
        );

    resolveLink
      .then(({ agencyId: id, agencyName: name, appUrl }) => {
        if (cancelled) return;
        setAgencyId(String(id));
        setAgencyName(name);
        setError(null);
        setLoading(false);

        // iOS Safari + Chrome on iOS share this path (WKWebView).
        // Android Chrome uses the intent URL below (app or Play Store).
        if (isIOS) {
          cancelAppOpen = openAppOrFallbackToStore({
            appUrl,
            onSkipStorePrompt: () => {
              suppressStorePromptRef.current = true;
            },
            onStoreFallback: () => {
              if (cancelled || suppressStorePromptRef.current) return;
              if (
                document.hidden ||
                document.visibilityState === "hidden" ||
                !document.hasFocus()
              ) {
                return;
              }
              setShowAppStorePrompt(true);
            },
          });
          return;
        }

        if (isMobile) {
          window.location.href = appUrl;
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      cancelAppOpen();
    };
  }, [enrollmentToken, practiceToken, isMobile]);

  const closeAppStorePrompt = () => {
    suppressStorePromptRef.current = true;
    setShowAppStorePrompt(false);
  };

  // If our store dialog somehow opened while the OS "Open in BioSyncIQ?" sheet
  // was up, dismiss it as soon as the app actually opens.
  useEffect(() => {
    if (!showAppStorePrompt) return undefined;

    const dismissIfAppOpened = () => {
      if (document.hidden || document.visibilityState === "hidden") {
        setShowAppStorePrompt(false);
      }
    };

    document.addEventListener("visibilitychange", dismissIfAppOpened);
    window.addEventListener("pagehide", dismissIfAppOpened);
    return () => {
      document.removeEventListener("visibilitychange", dismissIfAppOpened);
      window.removeEventListener("pagehide", dismissIfAppOpened);
    };
  }, [showAppStorePrompt]);

  const allowAppStoreOpen = async () => {
    const landingUrl = window.location.href;
    const storeUrl = buildAppStoreUrl();

    // 1) Copy first and wait until it finishes
    await copyTextToClipboard(landingUrl);

    // 2) Only then open the App Store
    window.location.assign(storeUrl);
  };

  const practiceDetailsHandler = () => {
    if (enrollmentToken) {
      navigate(
        `/patient-details?${buildEnrollmentQuery({
          enrollmentToken,
          agencyName,
        })}`,
      );
      return;
    }

    navigate(
      `/patient-details?agency_id=${agencyId}&agency_name=${encodeURIComponent(agencyName || "")}`,
    );
  };

  if (loading) {
    return (
      <BgBox>
        <Stack
          flex={1}
          alignItems="center"
          justifyContent="center"
          gap="16px"
          padding="24px"
        >
          <CircularProgressLoading />
          <Typography color="secondary" align="center">
            {enrollmentToken
              ? "Checking enrollment link..."
              : "Checking link..."}
          </Typography>
        </Stack>
      </BgBox>
    );
  }

  if (error) {
    return (
      <BgBox>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flex={1}
          padding="24px"
        >
          <Typography color="error" align="center">
            {error}
          </Typography>
        </Box>
        <BiosynciqFooter />
      </BgBox>
    );
  }

  return (
    <BgBox>
      <Box width="83px" height="80px">
        <img src={MccLogo} alt="mcc-logo" width="100%" height="100%" />
      </Box>

      <Box
        component="main"
        bgcolor="#fff"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="32px"
        height="100%"
        width="100%"
        boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08)"
        borderRadius="16px"
        padding="92px 24px"
        sx={{
          [theme.breakpoints.down("md")]: {
            padding: "24px 24px",
            flexDirection: "column",
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="32px"
        >
          <Box
            width="408px"
            height="437px"
            sx={{
              [theme.breakpoints.down("md")]: {
                height: "388px",
              },
              [theme.breakpoints.down("sm")]: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            <Box
              component="img"
              width="100%"
              sx={{
                [theme.breakpoints.down("sm")]: {
                  width: "85%",
                },
              }}
              height="100%"
              src={MobileAppImage}
              alt="BiosyncIQ app"
            />
          </Box>
        </Box>

        <Stack gap="16px">
          <Typography
            variant="h1"
            color="#124273"
            fontSize="4.5rem"
            sx={{
              [theme.breakpoints.down("sm")]: {
                fontSize: "38px",
              },
            }}
          >
            {agencyName}
          </Typography>
          <Typography
            variant="h5"
            color="rgba(0, 0, 0, 0.87)"
            fontSize="2rem"
            fontWeight={500}
          >
            Follow the steps to get registered with us{" "}
          </Typography>
          <Stack padding="24px 0" gap="8px">
            <Timeline
              sx={{
                padding: "6px 0",
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
                },
              }}
            >
              <TimelineItem>
                <TimelineSeparator>
                  <Box
                    width="24px"
                    height="24px"
                    borderRadius="50%"
                    bgcolor="#0C80F3"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="caption" color="#fff">
                      1
                    </Typography>
                  </Box>
                  <TimelineConnector sx={{ margin: "4px 0", width: "1px" }} />
                </TimelineSeparator>
                <TimelineContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "0 8px",
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Download and install our mobile application on your phone.
                  </Typography>
                  <CommonStoreButton
                    enrollmentToken={enrollmentToken}
                    token={practiceToken}
                  />
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineSeparator>
                  <Box
                    width="24px"
                    height="24px"
                    borderRadius="50%"
                    bgcolor="#0C80F3"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="caption" color="#fff">
                      2
                    </Typography>
                  </Box>
                </TimelineSeparator>
                <TimelineContent
                  sx={{
                    padding: "0 8px",
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Signup using our Practice ID {agencyId}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            </Timeline>
          </Stack>
        </Stack>
      </Box>

      <Dialog
        open={showAppStorePrompt}
        onClose={closeAppStorePrompt}
        aria-labelledby="open-app-store-title"
        aria-describedby="open-app-store-description"
      >
        <DialogTitle id="open-app-store-title">Open App Store?</DialogTitle>
        <DialogContent>
          <DialogContentText id="open-app-store-description">
            BiosyncIQ wants to open the App Store so you can download the
            mobile app.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAppStorePrompt} color="inherit">
            Cancel
          </Button>
          <Button onClick={allowAppStoreOpen} variant="contained" autoFocus>
            Allow
          </Button>
        </DialogActions>
      </Dialog>

      <BiosynciqFooter />
    </BgBox>
  );
};

export default AgencyQrCode;
